import memoize from 'memoizee';
import { ethers } from 'ethers';
import config from '@/constants/config';
import { notifyHandler, notifyNotification } from '@/init';
import { contractAbis, multicall_abi, multicall_address } from '@/constants/abis';
import { sleep } from '@/utils/Async';
import { toSignatureAwareMethodName } from '@/utils/Ethersjs';
import { gas as contractGas, contract as currentContract } from '@/contract';
import * as errorStore from '@/components/common/errorStore';

const getPendingWaitingMessage = (pendingWaitingMessage, txHash) => (
    pendingWaitingMessage.replace('[TX_URL]', `${config.explorerBaseUrl.tx}${txHash}`)
)

const callMethod = async ({
    contract, // e.g. currentContract
    contractName = 'swap',
    fromAddress, // e.g. contract.default_account
    methodName, // e.g. 'add_liquidity'
    params = [], // Array of params, if the method takes any
    extraData: {
        ethValue = undefined, // Amount of ETH transfered, if any
        ethereumContractGasEstimationFallback = undefined, // Either a number, or an object of shape { methodName, poolId } (with methodName being a method name on contractGas)
        bypassGasEstimationFailureWarning = undefined, // Used in conjunction w/ a warning modal, see OneSplit
    } = {},
    // Pass additional, optional parameters to update state around the tx
    vueState: {
        vueInstance = undefined, // Optional, pass if we want to modify state such as `estimateGas`, `waitingMessage`
        initialWaitingMessage = 'Please confirm transaction', // Message displayed before tx is confirmed
        pendingWaitingMessage = `Waiting for <a href="[TX_URL]">transaction</a> to confirm: no further action needed`, // Message displayed when tx is confirmed and pending; use [TX_URL] placeholder to link to the tx
    } = {},
    // ${config.explorerBaseUrl.tx}${sendTransactionPromise.hash}
}) => {
    if (typeof contract === 'undefined') {
        throw new Error('callMethod error: param `contract` required');
    }
    if (typeof fromAddress === 'undefined') {
        throw new Error('callMethod error: param `fromAddress` required');
    }
    if (typeof methodName === 'undefined') {
        throw new Error('callMethod error: param `methodName` required');
    }
    if (
        config.blockchainId === 'ethereum' &&
        typeof ethereumContractGasEstimationFallback !== 'number' &&
        typeof ethereumContractGasEstimationFallback !== 'object'
    ) {
        throw new Error('callMethod error: param `extraData.ethereumContractGasEstimationFallback` is required for Ethereum, and must be a number or an object');
    }
    if (
        config.blockchainId === 'ethereum' &&
        typeof ethereumContractGasEstimationFallback === 'object' &&
        (!ethereumContractGasEstimationFallback.methodName || !ethereumContractGasEstimationFallback.poolId)
    ) {
        throw new Error('callMethod error: if param `extraData.ethereumContractGasEstimationFallback` is an object, its shape must be { methodName, poolId }');
    }


    if (vueInstance) vueInstance.waitingMessage = initialWaitingMessage;
    const { dismiss } = notifyNotification(initialWaitingMessage)
    await sleep(100);

    let estimatedGas = 0;
    if (config.blockchainId === 'ethereum') {
        try {
            estimatedGas = await contract[contractName].methods[methodName](...params).estimateGas({
                from: fromAddress,
                gasPrice: vueInstance.gasPriceWei,
                gas: 1800000,
                ...(ethValue ? { value: ethValue } : {}),
            });
            vueInstance.estimateGas = estimatedGas;
            estimatedGas = (estimatedGas * 1.4).toFixed(0);
            vueInstance.errorEstimating = null;
            vueInstance.showFailedGasEstimationModal = false;
        } catch (e) {
            if (typeof ethereumContractGasEstimationFallback !== 'number') {
                estimatedGas = contractGas[ethereumContractGasEstimationFallback.methodName][ethereumContractGasEstimationFallback.poolId];
                vueInstance.estimateGas = estimatedGas;
            } else {
                vueInstance.estimateGas = ethereumContractGasEstimationFallback; // Number
            }

            vueInstance.errorEstimating = true;
            vueInstance.showFailedGasEstimationModal = true;
        }

        // If there's a gas estimation failure warning to display, and user hasn't already
        // bypassed it by clicking "proceed nonetheless", abort trade and let the modal show.
        if (vueInstance.showFailedGasEstimationModal && bypassGasEstimationFailureWarning === false) {
            dismiss();
            vueInstance.waitingMessage = '';
            vueInstance.show_loading = false;
            return;
        }
        vueInstance.showFailedGasEstimationModal = false;

        const sendTx = async () => (
            contract[contractName].methods[methodName](...params).send({
                from: fromAddress,
                gasPrice: vueInstance.gasPriceWei,
                gas: estimatedGas,
                ...(ethValue ? { value: ethValue } : {}),
            }).once('transactionHash', (hash) => {
                dismiss();
                notifyHandler(hash);
                vueInstance.waitingMessage = getPendingWaitingMessage(pendingWaitingMessage, hash);
            })
        );

        let receipt;

        try {
            receipt = await sendTx();
        } catch(err) {
            console.error(err);
            dismiss();
            errorStore.handleError(err);

            if(err.code == -32603) {
                await sleep(300)
                receipt = await sendTx();
            }
        }

        dismiss();
        vueInstance.waitingMessage = '';
        vueInstance.show_loading = false;

        return receipt;
    } else {
        console.log('callMethod', { contract, contractName, methodName, params, fromAddress });

        const isFakePlainPool = !!contract.crypto_zap;
        console.log('contract.crypto_zap?', isFakePlainPool);

        estimatedGas = await contract[(
            contractName === 'gauge' ? 'gaugeContract' :
            (isFakePlainPool && contractName !== 'gaugeContract') ? 'crypto_zap' : // TODO check if this is a reliable condition
            contractName
        )].methods[methodName](...params).estimateGas({
            from: fromAddress,
        });

        if (vueInstance) {
            vueInstance.estimateGas = estimatedGas / 2;
        }

        if (config.blockchainId === 'polygon') {
          estimatedGas = estimatedGas * 1.5
        }

        const signer = (
            contract.signer ||
            (contract.provider || new ethers.providers.Web3Provider(window.ethereum)).getSigner()
        );

        let contractAddress;
        let contractAbi;

        if (contractName === 'gauge') {
            contractAddress = contract.gaugeContract._address;
            contractAbi = contract.gaugeContract._jsonInterface;
        } else if (contractName === 'oldGauge') {
            contractAddress = contract.oldGauge._address;
            contractAbi = contract.oldGauge._jsonInterface;
        } else if (isFakePlainPool) {
            contractAddress = contract.crypto_zap._address;
            contractAbi = contract.crypto_zap._jsonInterface;
        } else {
            contractAddress =
                contractAbis[contract.currentContract][contractName === 'swap' ? 'swap_address' : 'deposit_address'];
            contractAbi =
                contractAbis[contract.currentContract][contractName === 'swap' ? 'swap_abi' : 'deposit_abi'];
        }

        const ethersContract = new ethers.Contract(
            contractAddress,
            contractAbi,
            signer
        );

        const ethersMethodName = toSignatureAwareMethodName({
            methodName,
            abi: contractAbi,
            params,
        });

        const sendTransactionPromise = await ethersContract[ethersMethodName](...params, {
            gasLimit: estimatedGas * 1.3 | 0,
            gasPrice: vueInstance.gasPriceWei,
        });

        dismiss();
        notifyHandler(sendTransactionPromise.hash); // TODO prob not needed in chain w/o bnc support
        if (vueInstance) {
            vueInstance.waitingMessage = getPendingWaitingMessage(pendingWaitingMessage, sendTransactionPromise.hash);
            const receipt = await sendTransactionPromise.wait();
            vueInstance.waitingMessage = '';
            vueInstance.show_loading = false;

            return receipt;
        }
    }
}

/**
 * @param {Array<{contract: Object, methodName: String, params: Array}>} callsConfig
 *
 * Returns an array of data.
 * If `metaData` is passed alongside any call, returns an array of objects of shape { data, metaData } instead.
 */
const multiCall = async (callsConfig) => {
    const defaultCallConfig = {
        contract: undefined, // e.g. currentContract
        methodName: undefined, // e.g. 'claimable_tokens'
        params: [], // Array of params, if the method takes any
        // Optional; any data to be passed alongside each call's results, for example to act as a marker
        // to easily identify what the call's results reference
        metaData: undefined,
    };

    const augmentedCallsConfig = callsConfig.map((config) => ({
        ...defaultCallConfig,
        ...config,
    }));

    // Validate configs
    for (const config of augmentedCallsConfig) {
        if (typeof config.contract !== 'object') {
            throw new Error('multiCall error: config parameter `contract` expects a contract object');
        }

        if (typeof config.methodName !== 'string') {
            throw new Error('multiCall error: config parameter `methodName` expects a contract method name');
        }

        if (!config.contract._address) {
            throw new Error('multiCall error: couldn’t find any `_address` property on config parameter `contract`; either the contract object passed in incorrect, or we need to make multiCall accept an optional address param to pass it manually ourselves when it’s not implicitly set on `contract`');
        }
    }

    const hasMetaData = augmentedCallsConfig.some(({ metaData }) => typeof metaData !== 'undefined');

    const calls = augmentedCallsConfig.map(({
        contract,
        methodName,
        params,
    }) => [
        contract._address,
        contract.methods[methodName](...params).encodeABI(),
    ]);

    const multicall = new web3.eth.Contract(multicall_abi, multicall_address);
    const { returnData } = await multicall.methods.aggregate(calls).call();

    if (returnData.length !== augmentedCallsConfig.length) {
        throw new Error('multiCall error: multiCall doesn’t support calls to methods with multiple outputs yet, please improve it :)');
    }

    const decodedData = returnData.map((hexData, i) => {
        const { contract, methodName, metaData } = augmentedCallsConfig[i];
        const abi = contract._jsonInterface;
        const outputSignature = abi.find(({ name }) => name === methodName).outputs[0];

        const data = currentContract.web3.eth.abi.decodeParameter(outputSignature.type, hexData);

        if (hasMetaData) return { data, metaData };
        return data;
    });

    return decodedData;
};

// Validates an Ethereum address, and resolves ENS names.
// Returns either a valid Ethereum address, or null if the input was invalid.
const getNormalizedEthereumAddress = memoize(async (input) => {
    const isValidAddress = currentContract.web3.utils.isAddress(input);
    if (isValidAddress) return input;

    // We can fail early if there's no dot in this string, it's a simple heuristic for invalid names
    if (!input.includes('.')) return null;

    try {
        const address = await currentContract.web3.eth.ens.getAddress(input);
        return address; // Will throw if name wasn't valid
    } catch (err) {}

    return null;
}, {
    promise: true,
    maxAge: 30 * 60 * 1000, // 30 min
});

export {
    callMethod,
    multiCall,
    getNormalizedEthereumAddress,
};
